import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { urls } from "@courses-pm2/api/src/ts/config";

type PM2PortalListResponse = {
    /**
     * Contains the internal SiteCore id of the portal.
     */
    id: string;

    /**
     * Specifies the display-friendly title of the portal.
     */
    title: string;

    /**
     * Specifies the primary domain used when accessing this portal.
     */
    primaryDomainName: string;

    /**
     * Specifies the list of alias domains available (these will all redirect to
     * the primary domain automatically).
     */
    aliasDomainNames: string[];

    /**
     * Specifies the isbn number associated with this portal.
     */
    uniqueId: string;

    /**
     * Optionally specifies a list of access keys (ie. isbn numbers) that's used
     * to grant access to this portal. If nothing has been supplied here, we
     * should fall back to using the uniqueId as a single access key.
     */
    accessKeys?: string[];
}[];

/**
 * Specification of data returned about each available portal.
 */
export type PM2Portal = {
    /**
     * Contains the internal SiteCore id of the portal.
     */
    id: string;

    /**
     * Specifies the display-friendly title of the portal.
     */
    title: string;

    /**
     * Specifies the primary domain used when accessing this portal.
     */
    primaryDomainName: string;

    /**
     * Specifies the list of alias domains available (these will all redirect to
     * the primary domain automatically).
     */
    aliasDomainNames: string[];

    /**
     * Contains the list of access keys (ie. isbn numbers) that's used to grant
     * access to this portal.
     */
    accessKeys: string[];
};

/**
 * Internal cache used to keep the list of portals available in memory for
 * future reference, so we don't fetch the list over and over.
 */
let cache: PM2Portal[] | undefined;

/**
 * Helper that'll connect fetch a complete list of portals created on the given
 * environment.
 */
export async function getPortals(): Promise<PM2Portal[]> {
    if (!cache) {
        const response = await axios.get<PM2PortalListResponse>(
            gGateway(`${urls.pm2}/v1/portals`),
            {
                headers: {
                    "X-PM2-Authorized": "False",
                    "X-PM2-Authentication-Type": "None",
                    "X-PM2-User-Role": "None",
                },
            }
        );

        cache = response.data.map((portal) => ({
            id: portal.id,
            title: portal.title,
            primaryDomainName: portal.primaryDomainName,
            aliasDomainNames: portal.aliasDomainNames,
            accessKeys: (
                portal.accessKeys && portal.accessKeys.length > 0
                    ? portal.accessKeys
                    : [portal.uniqueId]
            ),
        }));
    }

    return cache;
}
